/***************************************************************************
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/

import {
  NumberField,
  Switch,
  TextField,
  Heading,
  Slider,
} from '@adobe/react-spectrum'
import { useAtom } from 'jotai'
import { FormItem } from '../components/FormItem'
import { viewerAtom } from '../config'
import { RendererOption } from '@a3d-viewer/viewer'

function DebugPanel() {
  const [atom, setAtom] = useAtom(viewerAtom)

  function handleRendererOptionChange<
    K extends keyof typeof atom.rendererOptions,
  >(key: K, value: any) {
    setAtom({
      ...atom,
      rendererOptions: {
        ...atom.rendererOptions,
        [key]: {
          ...atom.rendererOptions[key],
          value: value,
        },
      },
    })
  }

  return (
    <>
      <Heading level={1}>Renderer Options</Heading>
      {Object.entries(atom.rendererOptions).map(([key, value]) => {
        const label = key
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, (s) => s.toUpperCase())

        const optionValue = value as RendererOption

        if (optionValue.type === 'number') {
          return (
            <FormItem key={key}>
              <NumberField
                label={label}
                value={optionValue.value}
                onChange={(v) =>
                  handleRendererOptionChange(key as any, Number(v))
                }
                step={0.01}
                width={'100%'}
              />
            </FormItem>
          )
        } else if (optionValue.type === 'boolean') {
          return (
            <FormItem key={key}>
              <Switch
                isSelected={optionValue.value}
                onChange={(v) => handleRendererOptionChange(key as any, v)}
              >
                {label}
              </Switch>
            </FormItem>
          )
        } else if (optionValue.type === 'string') {
          return (
            <FormItem key={key}>
              <TextField
                label={label}
                value={optionValue.value}
                onChange={(v) => handleRendererOptionChange(key as any, v)}
                width={'100%'}
              />
            </FormItem>
          )
        } else if (optionValue.type === 'slider') {
          return (
            <FormItem key={key}>
              <Slider
                label={label}
                value={optionValue.value}
                minValue={optionValue.min}
                maxValue={optionValue.max}
                step={optionValue.step ?? 0.01}
                onChange={(v) => handleRendererOptionChange(key as any, v)}
                showValueLabel={true}
                formatOptions={{ style: 'percent', minimumFractionDigits: 1 }}
                width={'100%'}
              />
            </FormItem>
          )
        }
        return null
      })}
    </>
  )
}

export { DebugPanel }
